import type { FunctionComponent } from 'react'
import { useState } from 'react'
import { type SetTagWidgetData, IWidgetComponentApi } from '@playbooks/action-cards-utils'

import StateManager from '../../services/stateManager'
import { UndoIcon } from '../../components/ui'
import { useTranslation } from '../../contexts/localization/Localization'
import { CustomButton } from '../../components/ui/CustomButton'
import { useAppContext } from '../../contexts/AppContext'
import useComponentsApiRegistry from '../../hooks/useComponentsApiRegistry'
import { ObjectStateType } from '@playbooks/core/src/interfaces'

interface SetTagWidgetProps {
  widget: SetTagWidgetData
}

/**
 * Widget for Set Tags
 */
const SetTagWidget: FunctionComponent<SetTagWidgetProps> = ({ widget }) => {
  const { update: updateState } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const { text: t } = useTranslation()
  const widgetState = StateManager.getOrCreate(widget, ObjectStateType.IsTagSet)

  useComponentsApiRegistry(widget.id, () => {
    return {
      activate: () => {
        handleButtonClick()
        return Promise.resolve()
      },
    } as IWidgetComponentApi
  })

  function handleButtonClick() {
    if (isLoading || !!widgetState?.isActive) {
      return
    }
    setIsLoading(true)
    try {
      // Update the Action Card state (widget value, tags)
      updateState(widgetState, 'isActive', true)
    } catch (error: unknown) {
      console.error(`Error while setting tag: ${error}`)
    } finally {
      setIsLoading(false)
    }
  }

  function undoSetTag() {
    // Update the Action Card state (widget value, tags)
    updateState(widgetState, 'isActive', false)
  }

  return (
    <div className="flex items-center">
      <CustomButton size="lg" isLoading={isLoading} onClick={handleButtonClick} disabled={!!widgetState?.isActive}>
        <>{widget.buttonLabel || t('setTagWidget_defaultButton')}</>
      </CustomButton>

      {widgetState?.isActive && (
        <CustomButton onClick={undoSetTag} type="button" className="ml-2" tooltip={t('genericAiWidget_undoTooltip')}>
          <UndoIcon className="w-4 h-4" />
        </CustomButton>
      )}
    </div>
  )
}

export default SetTagWidget
